import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hasFeature } from '../../utils/features';
import {
  FiHome,
  FiFilm,
  FiClock,
  FiVideo,
  FiMic,
  FiZap,
  FiList,
  FiRadio,
  FiTag,
  FiAlertTriangle,
  FiAlignLeft,
  FiType,
  FiVolume2,
  FiChevronsRight,
  FiSliders,
  FiBookOpen,
  FiEyeOff,
  FiStar,
  FiBarChart2,
  FiShare2,
  FiTv,
  FiBriefcase,
  FiActivity,
  FiSearch,
  FiLayout,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiScissors,
  FiHeadphones,
  FiGlobe,
  FiShield,
  FiAlertOctagon,
  FiCpu,
  FiTrendingUp,
  FiCompass,
  FiMonitor,
  FiEye,
  FiMessageSquare,
  FiImage,
} from 'react-icons/fi';
import { useT } from '../../utils/i18n';
import { useUIStore, useAuthStore } from '../../utils/store';

// ─── Nav structure ───────────────────────────────────────────────────────────

const NAV_GROUPS = [
  {
    labelKey: 'sidebar.group.videoTools',
    items: [
      { icon: FiVideo,    labelKey: 'sidebar.item.videoToScript', path: '/video-to-script' },
      { icon: FiScissors, labelKey: 'sidebar.item.videoToVideo',  path: '/video-to-video'  },
    ],
  },
  {
    labelKey: 'sidebar.group.anchorFormats',
    items: [
      { icon: FiMessageSquare, labelKey: 'sidebar.item.quickAI', path: '/quick-ai' },
      // Opt-in per user (switched on in User Management) — hidden for everyone else.
      { icon: FiImage, labelKey: 'sidebar.item.mediaAI', path: '/media-ai', requiresMediaAI: true },
      { icon: FiMic, labelKey: 'sidebar.item.newsAnchorFormats', path: '/anchor-formats' },
    ],
  },
  {
    labelKey: 'sidebar.group.breakingPlates',
    items: [
      { icon: FiLayout, labelKey: 'sidebar.item.breakingPlates', path: '/breaking-plates' },
      { icon: FiAlertTriangle, labelKey: 'sidebar.item.breakingPoints', path: '/breaking-points' },
    ],
  },
  {
    labelKey: 'sidebar.group.bulletinTops',
    items: [
      { icon: FiList, labelKey: 'sidebar.item.tops', path: '/tops' },
      { icon: FiRadio, labelKey: 'sidebar.item.newsBulletin', path: '/news-bulletin' },
      { icon: FiTag, labelKey: 'sidebar.item.headlinesSlugs', path: '/headlines-slugs' },
      { icon: FiZap, labelKey: 'sidebar.item.breakingNewsPresentation', path: '/breaking-news-presentation' },
    ],
  },
  {
    labelKey: 'sidebar.group.scriptsFormats',
    items: [
      { icon: FiAlignLeft, labelKey: 'sidebar.item.scrollings', path: '/scrollings' },
      { icon: FiType, labelKey: 'sidebar.item.anchorBGTitles', path: '/anchor-bg-titles' },
      { icon: FiVolume2, labelKey: 'sidebar.item.voiceOver', path: '/voice-over' },
      { icon: FiChevronsRight, labelKey: 'sidebar.item.speedNews', path: '/speed-news' },
      { icon: FiSliders, labelKey: 'sidebar.item.scriptModes', path: '/script-modes' },
    ],
  },
  {
    labelKey: 'sidebar.group.programs',
    items: [
      { icon: FiAlertOctagon, labelKey: 'sidebar.item.burningIssue', path: '/burning-issue' },
      { icon: FiTrendingUp, labelKey: 'sidebar.item.tcr', path: '/tcr' },
      { icon: FiCompass, labelKey: 'sidebar.item.worldNews', path: '/world-news' },
      { icon: FiEye, labelKey: 'sidebar.item.crimeStory', path: '/crime-story' },
    ],
  },
  {
    labelKey: 'sidebar.group.longFormats',
    items: [
      { icon: FiBookOpen, labelKey: 'sidebar.item.indepth', path: '/indepth' },
      { icon: FiEyeOff, labelKey: 'sidebar.item.offTheRecord', path: '/off-the-record' },
      { icon: FiStar, labelKey: 'sidebar.item.specialFocus', path: '/special-focus' },
      { icon: FiBarChart2, labelKey: 'sidebar.item.analysis', path: '/analysis' },
    ],
  },
  {
    labelKey: 'sidebar.group.socialPromo',
    items: [
      { icon: FiShare2, labelKey: 'sidebar.item.socialMedia', path: '/social-media' },
      { icon: FiTv, labelKey: 'sidebar.item.promoScript', path: '/promo-script' },
      { icon: FiMonitor, labelKey: 'sidebar.item.digitalAnchorVisual', path: '/digital-anchor-visual' },
    ],
  },
  {
    labelKey: 'sidebar.group.speciality',
    items: [
      { icon: FiBriefcase, labelKey: 'sidebar.item.commercialInterviews', path: '/commercial-interviews' },
      { icon: FiActivity, labelKey: 'sidebar.item.sports', path: '/sports' },
      { icon: FiSearch, labelKey: 'sidebar.item.conspiracyTheory', path: '/conspiracy-theory' },
    ],
  },
  {
    labelKey: 'sidebar.group.audioGeneration',
    items: [
      { icon: FiHeadphones, labelKey: 'sidebar.item.scriptToVoice',  path: '/script-to-voice', feature: 'scriptToVoice' },
      { icon: FiGlobe,      labelKey: 'sidebar.item.languageChange', path: '/language-change'   },
    ],
  },
];

// ─── Component ───────────────────────────────────────────────────────────────
//
// Responsive behavior:
//   Mobile  (< 768px) : hidden by default; opens as a fixed full-height overlay
//                        drawer (controlled by useUIStore.sidebarOpen, toggled
//                        by the hamburger icon in TopRibbon) with a semi-
//                        transparent backdrop that closes it on click.
//   Tablet  (768–1023px): collapsed icon-only rail (~60px) by default, with a
//                        hover tooltip per icon and a toggle button at the
//                        bottom to expand to full width (240px).
//   Desktop (>= 1024px) : always visible, full width, with labels.

export const Sidebar = () => {
  const location = useLocation();
  const t = useT();
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { user } = useAuthStore();
  const [tabletExpanded, setTabletExpanded] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const hasMediaAI = hasFeature(user, 'mediaAI');
  const hasFullStory = hasFeature(user, 'fullStory');
  const hasFullStoryVideo = hasFeature(user, 'fullStoryVideo');

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // Tailwind width classes applied per-breakpoint:
  //  - mobile: always full drawer width (280px) when open, translated off-screen when closed
  //  - tablet: collapsed (60px) unless tabletExpanded, then full (240px)
  //  - desktop: always full (240–260px)
  const tabletWidthClass = tabletExpanded ? 'md:w-60' : 'md:w-[60px]';

  return (
    <>
      {/* Mobile backdrop — semi-transparent, closes drawer on click */}
      {sidebarOpen && (
        <div
          onClick={toggleSidebar}
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 flex flex-col z-50
          w-[280px] transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:z-30 ${tabletWidthClass} lg:w-60 3xl:w-64`}
      >
        {/* Logo strip */}
        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center min-w-0 overflow-hidden">
            <img
              src="/Prime9 Logo.png"
              alt="Prime9"
              className="h-8 object-contain flex-shrink-0 max-w-[140px]"
            />
          </div>
          {/* Close button — mobile overlay only */}
          <button
            onClick={toggleSidebar}
            className="md:hidden text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Close sidebar"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 sidebar-scroll">
          {/* Dashboard */}
          <div className="px-3 mb-1">
            <NavItem icon={FiHome} label={t('nav.dashboard')} path="/dashboard" isActive={isActive('/dashboard')} collapsed={!tabletExpanded} />
            <NavItem icon={FiClock} label={t('nav.history')} path="/history" isActive={isActive('/history')} collapsed={!tabletExpanded} />
          </div>

          <div className="my-2 mx-3 border-t border-gray-100" />

          {/* Automation */}
          <div className="px-3 mb-1">
            <p
              className={`px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400 select-none ${
                tabletExpanded ? '' : 'md:hidden lg:block'
              }`}
            >
              {t('sidebar.group.automation')}
            </p>
            <NavItem icon={FiCpu} label={t('sidebar.item.pipeline')} path="/automation" isActive={isActive('/automation')} collapsed={!tabletExpanded} />
            {/* Opt-in per user (switched on in User Management) — hidden for everyone else. */}
            {hasFullStory && (
              <NavItem icon={FiFilm} label={t('sidebar.item.fullStory')} path="/full-story" isActive={isActive('/full-story')} collapsed={!tabletExpanded} />
            )}
            {hasFullStoryVideo && (
              <NavItem icon={FiVideo} label={t('sidebar.item.fullStoryVideo')} path="/full-story-video" isActive={isActive('/full-story-video')} collapsed={!tabletExpanded} />
            )}
          </div>

          <div className="my-2 mx-3 border-t border-gray-100" />

          {/* Content groups */}
          {NAV_GROUPS.map((group) => (
            <div key={group.labelKey} className="px-3 mb-1">
              <p
                className={`px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400 select-none ${
                  tabletExpanded ? '' : 'md:hidden lg:block'
                }`}
              >
                {t(group.labelKey)}
              </p>
              {group.items.filter((item) => (!item.requiresMediaAI || hasMediaAI) && (!item.feature || hasFeature(user, item.feature))).map((item) => (
                <NavItem
                  key={item.path}
                  icon={item.icon}
                  label={t(item.labelKey)}
                  path={item.path}
                  isActive={isActive(item.path)}
                  collapsed={!tabletExpanded}
                />
              ))}
              <div className="my-1 border-t border-gray-50" />
            </div>
          ))}

          {/* Admin-only section */}
          {isAdmin && (
            <div className="px-3 mb-1">
              <p className={`px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400 select-none ${tabletExpanded ? '' : 'md:hidden lg:block'}`}>
                Admin
              </p>
              <NavItem icon={FiShield}  label="Voice Artists"      path="/admin/voice-artists"   isActive={isActive('/admin/voice-artists')}   collapsed={!tabletExpanded} />
              <NavItem icon={FiGlobe}   label="Dubbing History"    path="/admin/dubbing-history" isActive={isActive('/admin/dubbing-history')} collapsed={!tabletExpanded} />
              <div className="my-1 border-t border-gray-50" />
            </div>
          )}
        </nav>

        {/* Tablet-only expand/collapse toggle — hidden on mobile (drawer) and desktop (always full) */}
        <button
          onClick={() => setTabletExpanded((v) => !v)}
          className="hidden md:flex lg:hidden items-center justify-center gap-2 px-3 py-3 border-t border-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
          title={tabletExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {tabletExpanded ? <FiChevronLeft size={16} /> : <FiChevronRight size={16} />}
          {tabletExpanded && <span className="text-xs font-medium">Collapse</span>}
        </button>
      </aside>
    </>
  );
};

// ─── NavItem sub-component ───────────────────────────────────────────────────
//
// `collapsed` (tablet icon-rail mode only — never true on mobile/desktop)
// hides the label and shows a hover tooltip instead.

const NavItem = ({ icon: Icon, label, path, isActive, collapsed = false }) => (
  <Link
    to={path}
    className={`relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-150 mb-0.5 group ${
      collapsed ? 'md:justify-center lg:justify-start' : ''
    } ${
      isActive
        ? 'bg-primary-50 text-primary-700 font-semibold'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`}
  >
    <Icon
      size={16}
      className={`flex-shrink-0 transition-colors ${
        isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'
      }`}
    />
    {/* Label always visible on mobile + desktop; hidden only on the
        tablet icon-rail (md range) when collapsed=true. */}
    <span className={`truncate leading-tight ${collapsed ? 'md:hidden lg:inline' : ''}`}>
      {label}
    </span>
    {isActive && (
      <span className={`ml-auto w-1 h-4 rounded-full bg-primary-500 flex-shrink-0 ${collapsed ? 'md:hidden lg:block' : ''}`} />
    )}

    {/* Tooltip — tablet collapsed rail only */}
    {collapsed && (
      <span
        role="tooltip"
        className="hidden md:group-hover:flex lg:group-hover:hidden absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-2.5 py-1.5 rounded-lg bg-gray-900 text-white text-xs whitespace-nowrap shadow-lg pointer-events-none"
      >
        {label}
      </span>
    )}
  </Link>
);
